/**
 * PassVectorRow · single row in the triage list.
 *
 * Shows the pass-vector's player IDs, distance, angle, confidence,
 * and current status; exposes Accept / Edit / Reject / Delete actions
 * with status-conditional visibility, an auto-jump-to-release button,
 * and inlines either the re-draw overlay (when editing) or the
 * harvest-status strip (when verified / corrected).
 *
 * Action-visibility rules (single source of truth — keep this in sync
 * with the backend endpoints' `_mutate_pass_vector` guards):
 *
 *   • unverified  → Accept · Edit · Reject
 *   • verified    →           Edit · Reject
 *   • corrected   →           Edit · Reject
 *   • rejected    → Accept · Edit · Delete            (Accept = restore)
 *   • source=manual gets the Delete shortcut even when not rejected.
 *
 * Extracted from PassVectorTriage.jsx in the Phase A · Step 5 refactor.
 */
import React, { useState } from "react";
import {
  ArrowRight, CheckCircle2, Loader2, Pencil, Target, Trash2, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

import { passVectorPreviewUrl } from "@/lib/api";
import { PreviewTile } from "@/components/common/PreviewTile";
import { PassHeightSparkline } from "./PassHeightSparkline";
import { STATUS_STYLES } from "./statusStyles";
import { PassVectorRedrawOverlay } from "./PassVectorRedrawOverlay";
import { HarvestStatusStrip } from "./HarvestStatusStrip";
import DiscAnnotationCanvas from "./DiscAnnotationCanvas";
import { isImpossibleDistance, maxAllowedPassLengthM } from "@/lib/fieldSanity";

export function PassVectorRow({
  vector, index, parent, clip,
  busy, editing,
  disabledByCreate = false,
  onAccept, onReject, onEdit, onDelete, onCancelEdit, onCorrected,
  onRetriggerHarvest, onJumpToRelease, onAnnotationChanged,
  // 🆕 Phase E v8c · The Edit overlay needs `onNudgeFrame` to install
  // its window-level ←/→ keydown listener. Pre-fix this prop wasn't
  // threaded down, so the listener early-returned and the coach
  // couldn't frame-step while RE-DRAW ACTIVE was on. Same prop
  // signature as the Create overlay in PassVectorTriage.jsx so both
  // entry-points behave identically.
  onNudgeFrame,
  // 🆕 Phase E v8c.8 (2026-02-28) · LIVE clip-local timestamp from the
  // sequence player. Pre-fix the Edit overlay was passed the STATIC
  // `v.timestamp_seconds` so the backdrop frame never updated when the
  // operator frame-stepped — they were clicking "blind" against a
  // frozen image. Wiring `liveClipLocalT` through gives the same
  // synchronous backdrop refresh the Create overlay already enjoys.
  liveClipLocalT,
}) {
  const v = vector;
  const [annotateOpen, setAnnotateOpen] = useState(false);
  const styles = STATUS_STYLES[v.status] || STATUS_STYLES.unverified;
  const isRejected = v.status === "rejected";
  const isApproved = v.status === "verified" || v.status === "corrected";
  const canJump = typeof v.timestamp_seconds === "number" && !!onJumpToRelease;
  const annotatedCount = (v.disc_annotations || []).length;

  // Confidence visual cue — coloured pill on the row header.
  const confPct = Math.round((v.confidence || 0) * 100);
  const confColour = confPct >= 80
    ? "text-emerald-300"
    : confPct >= 60
      ? "text-amber-300"
      : "text-rose-300";

  // 🆕 2026-02-28 v8b · Field-sanity check. When the measured pass
  // length exceeds the field's physical diagonal (Pythagoras +5 m
  // tolerance), surface a high-visibility warning chip so the
  // operator catches mis-pinned vectors before they pollute the ML
  // training set. Dims come from the calibration we already render
  // against (parent.calibration), falling back to the legacy 100×37
  // for sequences uploaded before Phase E v7.
  const fieldDims = {
    fieldLengthM: parent?.calibration?.field_length_m,
    fieldWidthM: parent?.calibration?.field_width_m,
  };
  const impossibleDistance = isImpossibleDistance(v.length_m, fieldDims);
  const maxAllowedM = maxAllowedPassLengthM(fieldDims);

  return (
    <li
      data-testid={`pass-vector-row-${v.id}`}
      className={`px-3 py-2.5 ${isRejected ? "opacity-50" : ""}`}
    >
      <div className="flex items-center gap-3">
        <button
          type="button"
          data-testid={`pass-vector-jump-${v.id}`}
          onClick={canJump ? onJumpToRelease : undefined}
          disabled={!canJump}
          title={canJump
            ? `Jump video to release moment (t=${v.timestamp_seconds.toFixed(2)} s clip-local)`
            : "No timestamp on this vector"}
          className={`w-6 h-6 rounded-sm bg-secondary border border-border flex items-center justify-center font-mono text-xs ${styles.dot.replace("bg-", "text-")} ${canJump ? "hover:bg-primary hover:text-primary-foreground transition-colors cursor-pointer" : "cursor-not-allowed opacity-60"}`}
        >
          {index}
        </button>

        {/* 🆕 2026-08 · Pass-Kontroll-Kachel: Release-Standbild mit
            eingezeichnetem Vektor-Pfeil — Klick vergrößert (Lightbox).
            Cache-Key aus den Pins: nach Edit lädt die Kachel frisch. */}
        {clip?.id && v.start_image_xy && v.end_image_xy && (
          <PreviewTile
            testId={`pass-preview-tile-${v.id}`}
            src={`${passVectorPreviewUrl(clip.id, v.id)}?k=${Math.round(v.start_image_xy.x)}-${Math.round(v.end_image_xy.x)}-${Math.round((v.timestamp_seconds || 0) * 10)}`}
            alt={`Pass-Vorschau ${(v.length_m ?? 0).toFixed(1)} m`}
            caption={`Pass #${v.thrower_player_id ?? "?"} → #${v.catcher_player_id ?? "?"} · ${(v.length_m ?? 0).toFixed(1)} m — Pfeil = gespeicherte Pins am Release-Frame`}
            tileClass="h-14 w-auto"
          />
        )}

        {/* 🆕 2026-08 · Pass-Höhenprofil: Flugkurven-Sparkline aus dem
            3D-Fit — Wurfhöhe und Distanz zusammen prüfen. */}
        <PassHeightSparkline
          testId={`pass-sparkline-${v.id}`}
          fit={v.trajectory_fit}
          lengthM={v.length_m}
        />

        <button
          type="button"
          onClick={canJump ? onJumpToRelease : undefined}
          disabled={!canJump}
          className={`min-w-0 flex-1 text-left ${canJump ? "hover:bg-accent/30 rounded-sm px-1 -mx-1 cursor-pointer" : ""}`}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs tabular-nums">
              <span className="text-pink-300 font-bold">#{v.thrower_player_id ?? "?"}</span>
              <ArrowRight className="inline w-3 h-3 mx-1 text-muted-foreground" />
              <span className="text-pink-300 font-bold">#{v.catcher_player_id ?? "?"}</span>
            </span>
            <span className="font-mono text-xs text-foreground tabular-nums">
              {(v.length_m ?? 0).toFixed(1)} m
            </span>
            {impossibleDistance && (
              <Badge
                data-testid={`pv-impossible-distance-${v.id}`}
                className="bg-rose-500/15 text-rose-300 border-rose-400/60 border font-mono text-[9px] uppercase tracking-widest"
                title={`This pass (${v.length_m.toFixed(1)} m) exceeds the field diagonal max of ${maxAllowedM.toFixed(1)} m. Please check your Release (R) and Catch (C) pins.`}
              >
                ⚠ Impossible · field limit
              </Badge>
            )}
            {v.is_extrapolated && (
              <Badge
                data-testid={`pv-extrapolated-${v.id}`}
                className="bg-amber-400/15 text-amber-200 border-amber-300/60 border font-mono text-[9px] uppercase tracking-widest"
                title="Dieser Pass liegt außerhalb der kalibrierten Zone. Füge über 'Re-Calibrate' weitere Feldsegmente hinzu für maximale Präzision."
              >
                ⚠ Extrapoliert
              </Badge>
            )}
            <span className="font-mono text-[10px] text-muted-foreground tabular-nums">
              @ {Math.round(v.angle_deg ?? 0)}°
            </span>
            {v.source === "auto" && (
              <span className={`font-mono text-[10px] tabular-nums ${confColour}`}>
                conf {confPct}%
              </span>
            )}
            {v.source === "manual" && (
              <Badge className="bg-pink-400/15 text-pink-300 border-pink-400/40 border font-mono text-[9px] uppercase tracking-widest">
                manual
              </Badge>
            )}
            <Badge className={`${styles.badge} border font-mono text-[9px] uppercase tracking-widest`}>
              {styles.label}
            </Badge>
          </div>
          {v.timestamp_seconds != null && (
            <div className="font-mono text-[10px] text-muted-foreground mt-0.5">
              release t≈{v.timestamp_seconds.toFixed(2)}s
              {v.verified_at && (
                <span className="ml-2">· verified {v.verified_at.slice(11, 19)}</span>
              )}
              {canJump && (
                <span className="ml-2 text-primary/80">· click to jump</span>
              )}
            </div>
          )}
        </button>

        {/* Action buttons — visibility depends on current status. */}
        {!editing && (
          <div className="flex items-center gap-1 shrink-0">
            {(v.status === "unverified" || v.status === "rejected") && (
              <Button
                data-testid={`pass-vector-accept-${v.id}`}
                size="sm" onClick={onAccept} disabled={busy}
                className="h-7 px-2 bg-emerald-400/15 text-emerald-300 hover:bg-emerald-400 hover:text-black border border-emerald-400/40 font-display font-bold uppercase tracking-widest text-[10px]"
                title="Accept this pass-vector as-is (status → verified)"
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : (
                  <><CheckCircle2 className="w-3 h-3 mr-1" /> Accept</>
                )}
              </Button>
            )}

            <Button
              data-testid={`pass-vector-edit-${v.id}`}
              size="sm" variant="outline"
              onClick={(e) => {
                // 🆕 Phase E v8c · Re-bind focus on the master canvas
                // container after click so the ←/→ keydown listener
                // (window-level inside <PassVectorRedrawOverlay>)
                // receives the events. Without this `document.activeElement`
                // stays on the EDIT button itself and the browser may
                // route ←/→ to the button-focus-ring (no-op visually)
                // instead of bubbling them to window.
                try { e.currentTarget?.blur?.(); } catch { /* defensive */ }
                onEdit?.();
              }}
              disabled={busy || disabledByCreate}
              className="h-7 px-2 border-amber-300/40 bg-amber-300/5 text-amber-200 hover:bg-amber-300 hover:text-black font-display font-bold uppercase tracking-widest text-[10px] disabled:opacity-40"
              title={disabledByCreate
                ? "Finish or cancel the New Pass overlay before editing existing rows"
                : "Re-draw the pixel endpoints (status → corrected)"}
            >
              <Pencil className="w-3 h-3 mr-1" /> Edit
            </Button>

            {!isRejected && (
              <Button
                data-testid={`pass-vector-reject-${v.id}`}
                size="sm" variant="outline"
                onClick={onReject} disabled={busy}
                className="h-7 px-2 border-rose-400/40 bg-rose-400/5 text-rose-300 hover:bg-rose-400 hover:text-black font-display font-bold uppercase tracking-widest text-[10px]"
                title="Reject this pass-vector (hidden from analytics)"
              >
                <X className="w-3 h-3 mr-1" /> Reject
              </Button>
            )}

            {/* Phase B · Step 2 — Native Disc Annotation. Only available
                on verified/corrected vectors (those have a harvested
                slice.mp4 the canvas can scrub frames from). */}
            {isApproved && (
              <Button
                data-testid={`pass-vector-annotate-disc-${v.id}`}
                size="sm" variant="outline"
                onClick={() => setAnnotateOpen((o) => !o)}
                disabled={busy}
                className={`h-7 px-2 font-display font-bold uppercase tracking-widest text-[10px] ${
                  annotateOpen
                    ? "bg-violet-400 text-black border-violet-400"
                    : "border-violet-400/40 bg-violet-400/5 text-violet-300 hover:bg-violet-400 hover:text-black"
                }`}
                title="Open the disc bounding-box annotation canvas"
              >
                <Target className="w-3 h-3 mr-1" />
                Annotate Disc
                {annotatedCount > 0 && (
                  <span className="ml-1 tabular-nums">({annotatedCount})</span>
                )}
              </Button>
            )}

            {(isRejected || v.source === "manual") && (
              <Button
                data-testid={`pass-vector-delete-${v.id}`}
                size="sm" variant="ghost"
                onClick={onDelete} disabled={busy}
                className="h-7 w-7 p-0 hover:bg-destructive/20 hover:text-destructive"
                title="Permanently remove this vector"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Inline re-draw editor (Edit mode). */}
      {editing && (
        <div className="mt-3 pt-3 border-t border-amber-300/30">
          <PassVectorRedrawOverlay
            clip={clip}
            parent={parent}
            initialStart={v.start_image_xy}
            initialEnd={v.end_image_xy}
            releaseTimestampSeconds={
              // 🆕 v8c.8 (2026-02-28) · Prefer LIVE clip-local t from
              // the sequence player so the backdrop frame inside the
              // overlay stays in sync with the operator's frame-step
              // and the +1s / -1s nudges. Fall back to the persisted
              // `v.timestamp_seconds` only when no live currentTime
              // is available (deep-link mount, legacy callers).
              typeof liveClipLocalT === "number" && Number.isFinite(liveClipLocalT)
                ? liveClipLocalT
                : v.timestamp_seconds
            }
            onNudgeFrame={onNudgeFrame}
            onCancel={onCancelEdit}
            onSubmit={onCorrected}
          />
        </div>
      )}

      {isApproved && !editing && (
        <HarvestStatusStrip
          vector={v}
          busy={busy}
          onRetrigger={onRetriggerHarvest}
        />
      )}

      {/* Phase B · Step 2 Native Disc Annotation Canvas — only rendered
          when the coach explicitly opens it via the Target button. Stays
          mounted across frame nav so the cached frame strip + draft
          state persist without an extra Mongo round-trip. */}
      {isApproved && annotateOpen && (
        <div className="mt-3 pt-3 border-t border-violet-400/30">
          <DiscAnnotationCanvas
            clip={clip}
            vector={v}
            onAnnotationChanged={onAnnotationChanged}
          />
        </div>
      )}
    </li>
  );
}
